import { getSubscriptions } from 'volto-juizi-subscribe/reducers/subscriptions_data';
import { getUser } from '@plone/volto/actions/users/users';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { useReactToPrint } from 'react-to-print';
import parse from 'html-react-parser';

const InvoiceReport = () => {
  const dispatch = useDispatch();
  const [populatedHTML, setPopulatedHTML] = useState('');
  const [today, setToday] = useState('');
  const location = useLocation();
  const [inv_date, setInvoiceDate] = useState('');
  const [inv_amount, setInvoiceAmount] = useState('');
  const componentRef = useRef();

  const user = useSelector((state) => state.users.user);
  const userId = useSelector((state) =>
    state.userSession.token ? jwtDecode(state.userSession.token).sub : '',
  );

  const { data: subscriptionsData, loaded: subscriptionsLoaded } = useSelector(
    (state) => state.subscriptions_data,
  );

  useEffect(() => {
    setToday(new Date().toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setInvoiceDate(searchParams.get('date'));
    setInvoiceAmount(searchParams.get('amount'));
  }, [location.search]);

  useEffect(() => {
    dispatch(getUser(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (!subscriptionsLoaded) {
      dispatch(getSubscriptions());
    }
  }, [dispatch, subscriptionsLoaded]);

  // Function to conditionally replace template variables and mark empty lines
  const processTemplate = (html, template_vars) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find all text nodes
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null,
      false,
    );

    const nodesToProcess = [];
    let node;
    while ((node = walker.nextNode())) {
      nodesToProcess.push(node);
    }

    // Process each text node
    nodesToProcess.forEach((textNode) => {
      let text = textNode.textContent;
      let hasEmptyVariable = false;

      // Check if this text contains any template variables
      template_vars.forEach(({ key, value }) => {
        if (text.includes(key)) {
          if (!value || value.trim() === '') {
            hasEmptyVariable = true;
          }
          text = text.replaceAll(key, value);
        }
      });

      // Update the text
      textNode.textContent = text;

      // If this line had an empty variable, mark the parent element for removal
      if (hasEmptyVariable) {
        // Find the closest parent that represents a "line" (p, div, span with br, etc)
        let parent = textNode.parentElement;

        // Mark parents up to the line level
        while (parent && parent !== doc.body) {
          parent.setAttribute('data-empty-line', 'true');

          // Stop at block-level elements
          if (['P', 'DIV', 'TD', 'LI'].includes(parent.tagName)) {
            break;
          }
          parent = parent.parentElement;
        }
      }
    });

    // Remove all marked elements
    const markedElements = doc.querySelectorAll('[data-empty-line="true"]');
    markedElements.forEach((elem) => {
      // Check if the entire element should be removed
      // (i.e., it doesn't have any content besides the empty variable)
      const textContent = elem.textContent.trim();

      // Remove if it's truly empty or only has labels with no values
      // Pattern: looks for cases like "VAT nr: " with nothing after
      const hasOnlyLabel =
        /^[^:]*:\s*$/.test(textContent) || textContent === '';

      if (hasOnlyLabel || textContent === '') {
        elem.remove();
      }
    });

    // Clean up any orphaned <br> tags (more than one consecutive)
    const allBrs = doc.querySelectorAll('br');
    allBrs.forEach((br) => {
      const next = br.nextSibling;
      if (next && next.nodeName === 'BR') {
        br.remove();
      }
    });

    return doc.body.innerHTML;
  };

  useEffect(() => {
    if (user && subscriptionsData?.data?.invoice_template) {
      const exclAmount = inv_amount
        ? (parseFloat(inv_amount) / 1.15).toFixed(2)
        : '0.00';
      const vatAmount = inv_amount
        ? (parseFloat(inv_amount) - parseFloat(exclAmount)).toFixed(2)
        : '0.00';
      const template_vars = [
        { key: '{{today}}', value: today },
        { key: '{{date}}', value: inv_date },
        {
          key: '{{amount}}',
          value: inv_amount ? parseFloat(inv_amount).toFixed(2) : '0.00',
        },
        { key: '{{user_id}}', value: userId || '' },
        // Added: user_name from user object
        {
          key: '{{user_name}}',
          value: user.fullname || user.full_name || '',
        },
        { key: '{{contact_number}}', value: user.contact_number || '' },
        { key: '{{user_email}}', value: user.email || '' },
        {
          key: '{{company_name}}',
          value: user.company_name || '',
        },
        {
          key: '{{company_address}}',
          value: user.company_address || '',
        },
        {
          key: '{{company_vat}}',
          value: user.company_vat || '',
        },
        {
          key: '{{subscription_type}}',
          value:
            user.subscription_type ||
            subscriptionsData?.data?.subscription_type ||
            '',
        },
        // Both aliases for exclusive amount
        { key: '{{excl_amount}}', value: exclAmount },
        { key: '{{amount_exclusive}}', value: exclAmount },
        { key: '{{vat_amount}}', value: vatAmount },
        // Added: vat_percentage as a variable rather than hardcoded
        { key: '{{vat_percentage}}', value: '15' },
      ];

      let html = subscriptionsData?.data?.invoice_template;

      // Process template with smart empty line removal
      html = processTemplate(html, template_vars);

      setPopulatedHTML(html);
    }
  }, [
    user,
    subscriptionsLoaded,
    inv_date,
    inv_amount,
    today,
    userId,
    subscriptionsData,
  ]);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `invoice_${inv_date}`,
  });

  return (
    <div className="invoice-wrapper">
      <div
        className="invoice-print-button"
        style={{
          display: 'flex',
          marginTop: '2rem',
          marginBottom: '1rem',
          justifyContent: 'center',
        }}
      >
        <button
          className="ui negative button primary button"
          onClick={handlePrint}
          style={{
            padding: '10px 20px',
            cursor: 'pointer',
          }}
        >
          Download as PDF
        </button>
      </div>

      <div
        className="invoice-print-info"
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '2rem',
        }}
      >
        <p className="help">
          If you need to change any of your information on the invoice, you can
          do so under the 'Personal Information' tab
        </p>
      </div>

      <div ref={componentRef}>
        <style type="text/css" media="print">
          {`
            @page {
              size: A4;
              margin: 10mm;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .invoice-page {
                box-shadow: none !important;
              }
            }
          `}
        </style>
        <div
          className="invoice-page"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '10mm',
            margin: '0 auto',
            backgroundColor: '#fff',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          }}
        >
          {populatedHTML && <div>{parse(populatedHTML)}</div>}
        </div>
      </div>
    </div>
  );
};

export default InvoiceReport;
